/**
 * Default Milestone Template — issue #460
 *
 * Fixed 6-stage checklist auto-seeded on every project created through the
 * two web creation paths (`createProject` in projectService.js,
 * `createProjectWithQuotation` in projectQuotationService.js). Jorge's
 * request: "Cada proyecto debe tener hitos: Cotización, Planeación, Compras,
 * Ejecución, Evaluación, Cierre" — the project's status stops being a lone
 * field that "starts lying" (#353) and becomes a checklist of phases that
 * gets marked off as work actually happens.
 *
 * MUST mirror `mcp-server/src/constants/milestoneTemplate.js` exactly — see
 * "Dual-Lane Parity" in CLAUDE.md. If you change a title/weight/order here,
 * change it there too.
 *
 * Design notes:
 * - Weights sum to 100 (weight_percentage is used for progress + optional
 *   milestone-based invoicing elsewhere in the app).
 * - `billable: false` for all six — auto-seeding a billing trigger without
 *   an explicit human decision is out of scope; Jorge can flip it on a
 *   per-milestone basis from the existing Hitos tab (MilestoneForm).
 * - This is a STARTING checklist, not a locked structure: every row created
 *   from this template is a normal `milestones` row, so it can be reordered
 *   (order_index, via MilestoneForm's "Orden" field) or removed (delete, via
 *   MilestoneList's kebab menu) exactly like any hand-created milestone. No
 *   new UI was built for this — the existing CRUD already covers "not every
 *   project wants all 6".
 * - Scope explicitly EXCLUDED here: wiring each milestone to its subsystem
 *   (e.g. having the "Compras" milestone show live purchase orders, or
 *   "Ejecución" show linked tasks inline). Those subsystems already exist
 *   (quotations, purchases/RFQ, tasks, project_closures) but this template
 *   only seeds the checklist row + a plain-text `description` pointing at
 *   the right module. Deep-linking is left for a follow-up issue.
 */

export const DEFAULT_MILESTONE_TEMPLATE = [
  {
    title: 'Cotización',
    order_index: 0,
    weight_percentage: 10,
    description: 'Elaboración y aprobación de la cotización inicial (módulo de Cotizaciones).',
  },
  {
    title: 'Planeación',
    order_index: 1,
    weight_percentage: 15,
    description: 'Planificación de alcance, cronograma y recursos antes de iniciar la ejecución.',
  },
  {
    title: 'Compras',
    order_index: 2,
    weight_percentage: 20,
    description: 'Abastecimiento de materiales y herramientas (órdenes de compra / RFQ).',
  },
  {
    title: 'Ejecución',
    order_index: 3,
    weight_percentage: 35,
    description: 'Trabajo de campo: tareas asignadas y ejecutadas por el equipo técnico.',
  },
  {
    title: 'Evaluación',
    order_index: 4,
    weight_percentage: 10,
    description: 'Revisión de calidad y cumplimiento de los criterios de aceptación.',
  },
  {
    title: 'Cierre',
    order_index: 5,
    weight_percentage: 10,
    description: 'Cierre formal del proyecto (asistente de cierre).',
  },
];

/**
 * Build insertable `milestones` rows for a brand-new project.
 * @param {string} projectId - Project UUID these milestones belong to.
 * @param {string} [userId] - created_by (nullable — MCP/web both have a user).
 * @returns {object[]} Rows ready for `supabase.from('milestones').insert(rows)`.
 */
export const buildDefaultMilestoneRows = (projectId, userId) =>
  DEFAULT_MILESTONE_TEMPLATE.map((m) => ({
    project_id: projectId,
    title: m.title,
    description: m.description,
    order_index: m.order_index,
    weight_percentage: m.weight_percentage,
    billable: false,
    status: 'pending',
    created_by: userId || null,
  }));

export default DEFAULT_MILESTONE_TEMPLATE;
